import * as React from 'react';

import { Button } from '@coursera/coursera-ui';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import type {
  RazorpayInstance,
  RazorpayInstanceOptions,
  RazorpayRawErrorResponse,
} from 'bundles/payments-common/utils/razorpayClientUtils';
import { RazorpayEvents, createInstance } from 'bundles/payments-common/utils/razorpayClientUtils';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })(Button);

type Props = Omit<RazorpayInstanceOptions, 'apiKey' | 'orderId'> & {
  apiKey?: string;
  orderId?: string;
  subscriptionId?: string;
  isSubscriptionUpdate?: boolean;
  onScriptFail: () => void;
  onPaymentFail: (res: RazorpayRawErrorResponse) => void;
  validateFn?: () => Promise<boolean>;
  disabled: boolean;
  rootClassName?: string;
};

type State = {
  instance?: RazorpayInstance;
  didClickBeforeReady: boolean;
};

class RazorpayButton extends React.LegacyComponentWithChildren<Props, State> {
  state: State = {
    instance: undefined,
    didClickBeforeReady: false,
  };

  async componentDidMount() {
    await this.setInstance();
  }

  async componentDidUpdate(prevProps: $TSFixMe) {
    const { apiKey, orderId, subscriptionId } = this.props;
    const { instance } = this.state;

    // apiKey / orderId / subscriptionId can change if toggling between sandbox and production
    if (prevProps.apiKey !== apiKey || prevProps.orderId !== orderId || prevProps.subscriptionId !== subscriptionId) {
      await this.setInstance();
      // If there's no orderId and no subscriptionId, close the Razorpay modal if it is currently open
    } else if (!orderId && !subscriptionId) {
      instance?.close();
    }
  }

  componentWillUnmount() {
    this.cleanUpInstance();
  }

  cleanUpInstance() {
    const { instance } = this.state;
    instance?.off(RazorpayEvents.PaymentFailed);
  }

  async setInstance() {
    const {
      orderId,
      subscriptionId,
      isSubscriptionUpdate,
      apiKey,
      onScriptFail,
      onPaymentFail,
      amount,
      currencyCode,
      title,
      description,
      logo,
      callback,
      callbackUrl,
      onModalClose,
    } = this.props;
    const { didClickBeforeReady } = this.state;

    this.cleanUpInstance();
    this.setState(() => ({ instance: undefined }));

    if (!(orderId || subscriptionId) || !apiKey) {
      return;
    }

    try {
      let instance: RazorpayInstance | undefined;
      const baseOptions = { amount, currencyCode, title, description, logo, callback, callbackUrl, onModalClose };
      if (orderId) {
        instance = await createInstance({ ...baseOptions, orderId: orderId!, apiKey: apiKey! });
      } else if (subscriptionId) {
        instance = await createInstance({
          ...baseOptions,
          subscriptionId: subscriptionId!,
          apiKey: apiKey!,
          isSubscriptionUpdate,
        });
      }
      instance?.on(RazorpayEvents.PaymentFailed, onPaymentFail);
      this.setState(() => ({ instance }));

      // To reduced perceived loading, user can click the button before the Razorpay instance is set up
      // If it was clicked previously, then open the modal immediately after setting up the instance
      if (didClickBeforeReady) {
        this.setState(() => ({ didClickBeforeReady: false }), this.handleClick);
      }
    } catch {
      onScriptFail();
    }
  }

  handleClick = async (e?: React.SyntheticEvent<HTMLButtonElement>): Promise<void> => {
    const { validateFn } = this.props;
    const { instance } = this.state;
    e?.preventDefault();

    if (!instance) {
      this.setState(() => ({ didClickBeforeReady: true }));
      return;
    }

    let isValidated = true;

    // Can pass in an optional validation function that runs before the modal opens
    if (validateFn) {
      isValidated = await validateFn();
    }

    if (isValidated) {
      instance?.open();
    }
  };

  render() {
    const { orderId, disabled, rootClassName, children } = this.props;
    const { instance, didClickBeforeReady } = this.state;

    return (
      <TrackedButton
        trackingName="razorpay_button"
        type="primary"
        trackingData={{ orderId, didClickBeforeReady: !instance }}
        size="sm"
        rootClassName={rootClassName}
        disabled={disabled || didClickBeforeReady}
        onClick={this.handleClick}
      >
        {children}
      </TrackedButton>
    );
  }
}

export default RazorpayButton;
